"use client";

import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  type Cell,
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/auth/permission-gate";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Columns3,
  Rows3,
  RotateCcw,
  Search,
  SearchX,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UserText } from "@/components/ui/user-text";
import { useShellTranslation } from "@/lib/shell/use-shell-translation";
import { useTextDirection } from "@/lib/i18n/use-text-direction";

import { SortableColumnItem } from "./sortable-column-item";
import { SortableTableHead } from "./sortable-table-head";
import { SortableFilterItem } from "./sortable-filter-item";
import { useTablePersistence } from "./use-table-persistence";
import { MultiSelectFilter } from "@/components/recipients/ui/multi-select-filter";
import { isAllSelected } from "@/lib/view-preferences/filter-sentinel";
import { DateFieldFilter } from "@/components/ui/date-field-filter";
import { SavedViewsMenu, type ViewState } from "@/components/ui/saved-views-menu";
import { useGridKeyboardNavigation } from "./use-grid-keyboard-navigation";
import { dragAccessibility } from "./drag-accessibility";
import { horizontalListSortingStrategyFor } from "./horizontal-sorting";
import { englishSingular } from "./english-noun";
import type {
  UnifiedDataTableProps,
  BulkActionConfig,
  CustomFilterConfig,
} from "./types";

const DEFAULT_INTERACTIVE_SELECTOR =
  'button, input, [role="switch"], [role="checkbox"], a, [data-radix-collection-item]';

/**
 * Was this row event raised OUTSIDE the row's own DOM? React bubbles a
 * portal's events (row menus, dialogs and sheets opened from a cell) through
 * the component tree to the row, although the portal's DOM sits elsewhere in
 * the page. Such an event is never a click or key press on the row.
 */
function isFromOutsideRow(event: React.SyntheticEvent<HTMLElement>): boolean {
  const target = event.target;
  return !(target instanceof Node) || !event.currentTarget.contains(target);
}

/**
 * Translate a facet's selection into a TanStack column-filter value.
 *
 * MultiSelectFilter collapses a full selection to the VIEW_FILTER_ALL
 * sentinel instead of enumerating every value — a facet with hundreds of
 * options would otherwise blow past URL / query-string / Redis limits. That
 * token is NOT a value any row holds, so handing it to TanStack made "Select
 * All" match nothing and empty the table (reported on /lms/assignments).
 *
 * Per the sentinel's own contract, "every value selected" === "no filter on
 * this facet", so it clears the filter rather than becoming it. An empty
 * selection clears it too, as before.
 *
 * Exported so the behaviour is testable without mounting the table.
 */
export function toColumnFilterValue(
  values: string[],
  options: ReadonlyArray<{ value: string }> = [],
): string[] | undefined {
  if (values.length === 0) return undefined;
  // Select All EXPANDS to every option value; it is not discarded.
  //
  // Returning undefined here was the first attempt and it was half right. It
  // stopped the table emptying, but the facet renders its checkboxes from the
  // SAME column filter value (`getFilterValue() ?? []`), so clearing it also
  // cleared the ticks — Select All appeared to do nothing at all. Reported on
  // /lms/sent-emails: "Select All not working, I can't even click it".
  //
  // Expanding satisfies both halves at once: every row still matches (the
  // column filter now lists every value a row can hold) AND the selection is
  // readable back, so every box shows ticked. The sentinel itself never
  // reaches a filterFn, which is what made it match nothing in the first place.
  if (isAllSelected(values)) {
    const all = options.map((o) => o.value);
    // No options to expand into (a facet whose list has not loaded yet) —
    // fall back to clearing rather than storing a token nothing can match.
    return all.length > 0 ? all : undefined;
  }
  return values;
}

/**
 * Plain text a column renders is user data: a name, a subject, an email
 * (RTL-04). It goes through <UserText truncate>, which gives it its own
 * direction: body cells are one line and clipped, and a left-to-right cell
 * clips an Arabic value at its START (its first words), as a right-to-left
 * cell does an English one.
 *
 * Text without a letter has no direction of its own. A plain number or a date
 * written in digits ("97", "2026-09-26", "1,234") stays exactly as it was. But
 * a sign or spaces between digit groups is laid out by the cell's direction:
 * on an Arabic page "+971501234567" read "971501234567+", "+971 50 123 4567"
 * read "4567 123 50 971+", "09:00 - 17:00" read "17:00 - 09:00" and "-12"
 * read "12-". So does a range joined without spaces by a dash, tilde, arrow or
 * times sign: "09:00–17:00" read "17:00–09:00". (A hyphen-minus, colon,
 * slash, comma or dot between two numbers joins them into one, so "1-5",
 * "12:30" and "1,234" never moved.) Those read left to right in an inline
 * <bdi dir="ltr">: inline, so a numeric column keeps its alignment.
 */
const LETTER = /\p{L}/u;
// A plus sign anywhere, whitespace BETWEEN characters, a leading minus sign
// before a digit (hyphen-minus or U+2212), or two digits joined by a neutral
// the bidi algorithm does not treat as part of a number: the dashes
// U+2010-U+2015 (hyphen, en and em dash among them), a tilde, a right arrow
// or a times sign.
const REORDERS_RIGHT_TO_LEFT = /\+|\S\s+\S|^\s*[-\u2212]\d|\d\s*[\u2010-\u2015~\u2192\u00d7]\s*\d/;

function asUserText(content: React.ReactNode): React.ReactNode {
  if (typeof content !== "string") return content;
  if (LETTER.test(content)) return <UserText truncate>{content}</UserText>;
  if (REORDERS_RIGHT_TO_LEFT.test(content)) return <UserText dir="ltr">{content}</UserText>;
  return content;
}

type CellRenderer = (context: Record<string, unknown>) => React.ReactNode;

// One wrapper component per renderer function, so a renderer keeps exactly
// what flexRender gives it: it renders as a component of its own (its hooks
// stay its own), and a different renderer mounts afresh.
const textAwareCells = new WeakMap<CellRenderer, React.FC<Record<string, unknown>>>();

function textAwareCell(render: CellRenderer): React.FC<Record<string, unknown>> {
  let component = textAwareCells.get(render);
  if (!component) {
    const TextAwareCell: React.FC<Record<string, unknown>> = (context) => asUserText(render(context));
    TextAwareCell.displayName = `TextAwareCell(${render.name || "cell"})`;
    component = TextAwareCell;
    textAwareCells.set(render, component);
  }
  return component;
}

/** A body cell: what flexRender renders, with plain text rendered as user text. */
function renderBodyCell<TData, TValue>(cell: Cell<TData, TValue>): React.ReactNode {
  const render: unknown = cell.column.columnDef.cell;
  const context = cell.getContext();
  const isClassComponent =
    typeof render === "function" &&
    Boolean((render as { prototype?: { isReactComponent?: unknown } }).prototype?.isReactComponent);
  if (typeof render === "function" && !isClassComponent) {
    return React.createElement(
      textAwareCell(render as CellRenderer),
      context as unknown as Record<string, unknown>,
    );
  }
  // A class, memo or forwardRef component, or a constant: flexRender as before.
  return asUserText(flexRender(cell.column.columnDef.cell, context));
}

export function UnifiedDataTable<TData, TValue, TSearchState = unknown>({
  columns,
  data,
  localStoragePrefix,
  columnDisplayNames = {},
  columnDefaults,
  enableSearch = true,
  searchPlaceholder,
  searchHintText,
  globalFilterFn = "includesString",
  onSearchChange,
  searchValue,
  onSortChange,
  onFilterChange,
  onFilteredRowsChange,
  onFilterStateChange,
  filters,
  filterDefaults,
  customFilters,
  customFiltersActive,
  onClearCustomFilters,
  dateFilters,
  enableDensityToggle = true,
  enableRowSelection = false,
  externalRowSelection,
  onExternalRowSelectionChange,
  externalAllAcrossPagesSelected,
  onExternalAllAcrossPagesSelectedChange,
  bulkActions,
  renderBulkActions,
  rowBehavior,
  getRowId,
  advancedSearch,
  pagination,
  clientPagination,
  defaultSorting,
  tableMeta,
  emptyState,
  renderToolbarLeft,
  renderToolbarCenter,
  renderToolbarRight,
  renderFooterStatus,
  sanitizePersistedState,
  maxHeight = "calc(100vh - 380px)",
  minHeight,
  fillHeight = false,
  card = true,
  noResultsText,
  filteredEmptyHint,
  keyboardNavigation = false,
  hasExternalFilters = false,
  errorState,
  isFetching = false,
  entityNoun = 'items',
  savedViewsKey,
}: UnifiedDataTableProps<TData, TValue, TSearchState>) {
  // --- #415 a11y: id for the "skip to pagination" fragment target ---
  // Unique per mounted table (this component appears twice on some pages), so
  // the skip link can never land on another table's footer. `useId()` emits
  // colons (":r0:"); they are legal in an id and in an href fragment but not in
  // a CSS selector, and the link resolves its target with `getElementById`.
  // Stripped anyway so a consumer can safely write `#udt-pagination-...` in CSS.
  const a11yInstanceId = React.useId().replace(/:/g, "");
  const paginationRegionId = `udt-pagination-${a11yInstanceId}`;

  // --- Words (locale audit I-02) ---
  // The table's own chrome is in messages/shell. Outside the I18nProvider
  // (a spec, a portal) this `t` answers in English, as the literals did.
  const { t, locale } = useShellTranslation();
  // `entityNoun` is ENGLISH text from the page ("recipients"). It is spliced
  // only into English sentences, as a real plural: `singular` is its English
  // singular ("1 recipient", "1 reply", "1 person"). Every other language
  // counts "items" and "results" in its own words, with its own plural forms,
  // so an Arabic sentence never carries an English word.
  const nounIsReadable = locale === "en";
  const singularNoun = englishSingular(entityNoun);
  const countLabel = (count: number) =>
    nounIsReadable
      ? t("shell.table.countWithNoun", { count, noun: entityNoun, singular: singularNoun })
      : t("shell.table.count", { count });
  // The resize handles sit at each header's logical end (its LEFT edge in
  // Arabic); TanStack must know which way a drag widens a column.
  const textDirection = useTextDirection();

  // --- Persistence Hook ---
  const isControlled = externalRowSelection !== undefined;
  const persistence = useTablePersistence({
    prefix: localStoragePrefix,
    columnDefaults,
    filterDefaults,
    defaultSorting,
    // When the parent wired `onSortChange` it owns sorting server-side via a
    // URL/state query param — don't let localStorage restore a stale sort
    // that the BE query never received (header arrow would lie). See the
    // `urlControlledSorting` doc in use-table-persistence.ts.
    urlControlledSorting: !!onSortChange,
    isControlled,
    sanitizePersistedState,
  });

  // --- Density state (self-contained, localStorage-backed) ---
  type Density = "compact" | "default" | "comfortable";
  const densityKey = `${localStoragePrefix}-density`;
  const [density, setDensity] = React.useState<Density>("compact");
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(densityKey);
    if (stored === "compact" || stored === "default" || stored === "comfortable") {
      setDensity(stored);
    }
  }, [densityKey]);
  const cycleDensity = React.useCallback(() => {
    setDensity((prev) => {
      const next: Density =
        prev === "compact" ? "default" : prev === "default" ? "comfortable" : "compact";
      if (typeof window !== "undefined") localStorage.setItem(densityKey, next);
      return next;
    });
  }, [densityKey]);
  // Every density enforces both `tr` height AND `td` padding so rows render
  // at a uniform height regardless of cell content. Without this default, a
  // long wrapping title in one cell would push the row taller than its
  // siblings (B5 / 2026-05-06 phishing-campaigns audit).
  const densityClass =
    density === "compact"
      ? "[&_tbody_tr]:h-8 [&_td]:py-1 [&_td]:px-2"
      : density === "comfortable"
        ? "[&_tbody_tr]:h-16 [&_td]:py-3 [&_td]:px-4"
        : "[&_tbody_tr]:h-14 [&_td]:py-2 [&_td]:px-3";

  // --- Reconcile new filters into persisted state ---
  // Both column-bound filters (`filters`) and custom filters (`customFilters`)
  // share the same visibility/order namespace so they can be toggled +
  // reordered together in the Filters dropdown.
  React.useEffect(() => {
    if (
      (!filters && !customFilters && !dateFilters) ||
      !persistence.mounted
    )
      return;
    // Build the canonical declared order: column-bound filters first, then
    // custom filters, then date filters (matching the toolbar render order
    // below). Each entry carries enough info to derive its default
    // visibility. Date filters get defaultVisible=true so existing
    // operators see no behavior change on first encounter — they CAN
    // toggle them off via the Filters dropdown going forward.
    const declared: { id: string; defaultVisible: boolean }[] = [
      ...(filters ?? []).map((f) => ({
        id: f.id,
        defaultVisible:
          filterDefaults?.visibility?.[f.id] ?? f.defaultVisible ?? false,
      })),
      ...(customFilters ?? []).map((f) => ({
        id: f.id,
        defaultVisible:
          filterDefaults?.visibility?.[f.id] ?? f.defaultVisible ?? true,
      })),
      ...(dateFilters ?? []).map((d) => ({
        id: d.key,
        defaultVisible: filterDefaults?.visibility?.[d.key] ?? true,
      })),
    ];
    const allIds = declared.map((d) => d.id);
    const missingFromOrder = allIds.filter(
      (id) => !persistence.filterOrder.includes(id),
    );
    if (missingFromOrder.length > 0) {
      // Insert new filters at their declared position (based on declared order)
      const newOrder = [...persistence.filterOrder];
      for (const id of missingFromOrder) {
        const declaredIndex = allIds.indexOf(id);
        // Find the right insertion point relative to existing items
        let insertAt = newOrder.length;
        for (let i = declaredIndex - 1; i >= 0; i--) {
          const prevId = allIds[i];
          const existingIdx = newOrder.indexOf(prevId);
          if (existingIdx !== -1) {
            insertAt = existingIdx + 1;
            break;
          }
        }
        newOrder.splice(insertAt, 0, id);
      }
      persistence.setFilterOrder(newOrder);
      // Set visibility for new filters based on their defaultVisible or filterDefaults
      const visUpdates: Record<string, boolean> = {};
      for (const id of missingFromOrder) {
        const entry = declared.find((d) => d.id === id);
        visUpdates[id] = entry?.defaultVisible ?? false;
      }
      persistence.setFilterVisibility((prev) => ({ ...prev, ...visUpdates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, customFilters, dateFilters, persistence.mounted, persistence.filterOrder]);

  // --- Row Selection (controlled vs uncontrolled) ---
  const rowSelection = isControlled
    ? externalRowSelection
    : persistence.internalRowSelection;
  const setRowSelection = React.useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      const newSelection =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      if (isControlled) {
        onExternalRowSelectionChange?.(newSelection);
      } else {
        persistence.setInternalRowSelection(newSelection);
      }
    },
    [isControlled, rowSelection, onExternalRowSelectionChange, persistence],
  );

  const clearSelection = React.useCallback(() => {
    setRowSelection({});
    setIsAllAcrossPagesSelected(false);
    // setIsAllAcrossPagesSelected is declared AFTER this useCallback in the
    // component body (the controlled-vs-uncontrolled wiring needs filteredRowCount
    // first). Listing it in deps would compile to a TDZ access on the const at
    // line ~524 — Next.js production build fails with
    // "Cannot access 'setIsAllAcrossPagesSelected' before initialization".
    // It's a useCallback that's stable across renders anyway, so the dep is
    // semantically a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRowSelection]);

  // --- Search debounce (500ms) ---
  // CONTROLLED search: when the parent passes `searchValue` it owns the term
  // (typically in the URL), so the URL is the single source of truth. The input
  // then reads from `searchValue`, and typing does NOT persist the term to
  // localStorage — this is what fixes the cross-navigation desync where the box
  // showed a stale localStorage term that no longer matched the URL filter.
  const isControlledSearch = searchValue !== undefined;
  const [searchInputValue, setSearchInputValue] = React.useState(
    isControlledSearch ? searchValue : (persistence.globalFilter ?? ""),
  );
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchInputValue(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        // In controlled mode the URL is the source of truth — don't shadow the
        // term in localStorage (that copy is what desyncs across navigation).
        if (!isControlledSearch) persistence.setGlobalFilter(value);
        // Server-paginated callers (isServerPaginated) rely on this to
        // route the search value to their parent server query. See
        // UnifiedDataTableProps.onSearchChange JSDoc — without it,
        // server-paginated tables silently swallow the input because
        // the table's own globalFilter is pinned to "" at line below.
        if (onSearchChange) onSearchChange(value);
      }, 500);
    },
    [persistence, onSearchChange, isControlledSearch],
  );

  // Mirror the EXTERNAL search source into the visible input:
  //   - controlled: the parent-owned `searchValue` (URL), so navigating back to
  //     a list WITHOUT `?search=` correctly clears the box (and a restored
  //     `?search=` re-fills it) — box and applied filter always match,
  //   - uncontrolled: the persisted `globalFilter` (mount-time load of a saved
  //     term so the box shows what is actually filtering the list; prod
  //     2026-06-26: a persisted "Search groups" term filtered to empty while the
  //     input looked blank, reading as "no groups exist"),
  //   - the X / "Clear filters" button setting it back to "",
  //   - a parent-driven programmatic set.
  // The user's own typing is owned by the 500ms debounce and is never echoed
  // back here for the uncontrolled path: `persistence.globalFilter` only changes
  // AFTER the debounce flushes, by which point it already equals
  // `searchInputValue`, so this effect no-ops for in-flight keystrokes.
  const externalSearch = isControlledSearch
    ? searchValue
    : (persistence.globalFilter ?? "");
  React.useEffect(() => {
    if (externalSearch !== searchInputValue) {
      setSearchInputValue(externalSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSearch]);

  // --- Advanced Search ---
  const [advancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);
  // Controlled mode wins when the consumer supplies BOTH state + setter
  // (see AdvancedSearchConfig.state). This lets parent pages clear the
  // filter externally and the trigger badge stays in sync — internal
  // persistence in `useTablePersistence` would otherwise hold a stale copy
  // because nothing tells it to drop the in-memory value when localStorage
  // is cleared from outside the table.
  const isAdvancedSearchControlled =
    advancedSearch?.state !== undefined &&
    advancedSearch?.onStateChange !== undefined;
  // Persisted state is untyped-at-load (deserialized JSON) — narrow to the
  // consumer's TSearchState at the component boundary. Consumer owns the shape.
  const advancedSearchState = (
    isAdvancedSearchControlled
      ? (advancedSearch!.state ?? advancedSearch!.emptyState)
      : (persistence.advancedSearchState ?? advancedSearch?.emptyState)
  ) as TSearchState | undefined;
  const setAdvancedSearchState = (
    isAdvancedSearchControlled
      ? advancedSearch!.onStateChange!
      : persistence.setAdvancedSearchState
  ) as (next: TSearchState) => void;

  const filteredData = React.useMemo(() => {
    if (!advancedSearch || !advancedSearchState) return data;
    return advancedSearch.applySearch(data, advancedSearchState);
  }, [data, advancedSearch, advancedSearchState]);

  const activeAdvancedSearchCount = React.useMemo(() => {
    if (!advancedSearch || !advancedSearchState) return 0;
    const conditions = (advancedSearchState as { conditions?: unknown }).conditions;
    if (!Array.isArray(conditions)) return 0;
    return (conditions as { operator?: string; value?: string }[]).filter(
      (c) =>
        c.operator === "is_empty" ||
        c.operator === "is_not_empty" ||
        (c.value && c.value.trim() !== ""),
    ).length;
  }, [advancedSearch, advancedSearchState]);

  // --- DnD Sensors (standardized: 8px activation distance) ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // --- Bulk Action Confirmation Dialog ---
  const [confirmAction, setConfirmAction] =
    React.useState<BulkActionConfig<TData> | null>(null);

  // --- React Table ---
  // When server-side pagination is active, the server handles ALL filtering.
  // Disable client-side getFilteredRowModel and globalFilter to prevent
  // double-filtering that causes rows to disappear.
  const isServerPaginated = !!pagination;

  // Wrap setSorting so we can fan out to the optional `onSortChange` prop.
  // Server-paginated callers wire their server-query sort key to this so
  // header-chevron clicks actually reach the BE (not just localStorage).
  const handleSortingChange = React.useCallback(
    (
      updaterOrValue:
        | import("@tanstack/react-table").SortingState
        | ((
            old: import("@tanstack/react-table").SortingState,
          ) => import("@tanstack/react-table").SortingState),
    ) => {
      const next =
        typeof updaterOrValue === "function"
          ? (
              updaterOrValue as (
                old: import("@tanstack/react-table").SortingState,
              ) => import("@tanstack/react-table").SortingState
            )(persistence.sorting)
          : updaterOrValue;
      persistence.setSorting(next);
      if (onSortChange) {
        const first = next[0];
        onSortChange(first ? { id: first.id, desc: first.desc } : null);
      }
    },
    [persistence, onSortChange],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    // Without it TanStack assumes left to right, and dragging an Arabic
    // column's edge outwards (to the left) shrank it.
    columnResizeDirection: textDirection,
    enableRowSelection,
    // Server-controlled tables (those that wire `onSortChange`) must never
    // drop to an unsorted state via a header click. With the default
    // `enableSortingRemoval: true`, TanStack's cycle is none→asc→desc→none,
    // so a column seeded via `defaultSorting` (which starts mid-cycle at
    // `desc`) jumps straight to "none" on the first click — `onSortChange`
    // fires `null`, the parent snaps back to its default, and the header
    // chevron and the BE query desync (prod "status DESC not working"
    // 2026-05-17). Collapsing the cycle to a clean desc⇄asc toggle keeps the
    // table sort state and the parent server-query state in lockstep.
    enableSortingRemoval: onSortChange ? false : undefined,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: (updater) => {
      persistence.setColumnFilters(updater);
      // Server-side filter wiring: read the resolved next state and bubble
      // it up so server-paginated tables can update their query params.
      // Without this, filter chip clicks only mutate localStorage and the
      // BE never refetches with the new filter (audit-log tickets
      // 28af9f12 + 0cfc22f9).
      if (onFilterChange) {
        const next =
          typeof updater === 'function'
            ? updater(persistence.columnFilters)
            : updater;
        onFilterChange(next);
      }
    },
    onColumnSizingChange: persistence.setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: persistence.setGlobalFilter,
    ...(isServerPaginated ? {} : { globalFilterFn }),
    getCoreRowModel: getCoreRowModel(),
    // When the caller is server-paginated AND wired its server-sort query
    // param via `onSortChange`, sorting is the SERVER's job — `manualSorting`
    // tells TanStack to leave row order untouched and just report header
    // clicks. Without this, `getSortedRowModel()` re-sorts only the current
    // page client-side, so a status sort "works" only when every row fits on
    // one page (prod report 2026-05-16). Server-paginated tables that did NOT
    // wire `onSortChange` keep client sorting (better than no sort at all).
    manualSorting: isServerPaginated && !!onSortChange,
    getSortedRowModel: getSortedRowModel(),
    ...(isServerPaginated ? {} : { getFilteredRowModel: getFilteredRowModel() }),
    onColumnVisibilityChange: persistence.setColumnVisibility,
    onColumnOrderChange: persistence.setColumnOrder,
    ...(getRowId ? { getRowId: getRowId as (row: TData) => string } : {}),
    // Pagination: external control when pagination prop provided, otherwise show all
    ...(pagination
      ? {
          manualPagination: true,
          pageCount: pagination.totalPages,
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: (
            updater:
              | ((prev: { pageIndex: number; pageSize: number }) => {
                  pageIndex: number;
                  pageSize: number;
                })
              | { pageIndex: number; pageSize: number },
          ) => {
            const current = {
              pageIndex: pagination.page - 1,
              pageSize: pagination.pageSize,
            };
            const next =
              typeof updater === "function" ? updater(current) : updater;
            if (next.pageIndex !== current.pageIndex) {
              pagination.onPageChange(next.pageIndex + 1);
            }
            if (next.pageSize !== current.pageSize) {
              pagination.onPageSizeChange(next.pageSize);
            }
          },
        }
      : {
          // No pagination prop — render all data, no pagination controls
          getPaginationRowModel: getPaginationRowModel(),
        }),
    defaultColumn: {
      size: 150,
      minSize: 80,
      maxSize: 500,
    },
    initialState: {
      columnPinning: {
        left: columnDefaults?.pinned?.left ?? [],
        right: columnDefaults?.pinned?.right ?? [],
      },
      ...(pagination
        ? {}
        : clientPagination
          ? { pagination: { pageSize: clientPagination.pageSize ?? 25 } }
          : { pagination: { pageSize: 9999 } }),
    },
    state: {
      sorting: persistence.sorting,
      // Always pass columnFilters for UI display (filter chips). Client-side filtering
      // is disabled via getFilteredRowModel being absent when server-paginated.
      columnFilters: persistence.columnFilters,
      columnVisibility: persistence.columnVisibility,
      columnOrder: persistence.columnOrder,
      columnSizing: persistence.columnSizing,
      rowSelection,
      globalFilter: isServerPaginated ? "" : persistence.globalFilter,
      ...(pagination && {
        pagination: {
          pageIndex: pagination.page - 1,
          pageSize: pagination.pageSize,
        },
      }),
    },
    meta: tableMeta,
  });

  // Prune selection ids that are no longer in the data. After a PARTIAL bulk
  // action (e.g. Release 5 of 10, 5 remain queued) the removed rows leave the
  // data on refetch, but TanStack keeps their rowSelection entries — so the raw
  // selected-count (and the bulk bar) would stay stale until manually cleared
  // (#1791). Client-side only; server pages can't enumerate every id.
  React.useEffect(() => {
    if (isServerPaginated) return;
    const present = new Set(table.getCoreRowModel().rows.map((r) => r.id));
    setRowSelection((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next: RowSelectionState = {};
      for (const id of keys) {
        if (present.has(id)) next[id] = prev[id];
        else changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Expose the client-side FILTERED rows so a page can compute its summary/KPI
  // cards from the SAME subset the table shows (opt-in via onFilteredRowsChange;
  // no-op for server-paginated tables — they filter on the server, and calling
  // getFilteredRowModel() there is unconfigured). RENDER-LOOP SAFE: the effect
  // is keyed on a signature of the filtered row ids, NOT the rows array
  // identity, so a parent that stores the emitted rows in state does not re-fire
  // this effect (same filtered set → same signature). The callback is read via
  // a ref so its identity is not a dependency.
  const onFilteredRowsChangeRef = React.useRef(onFilteredRowsChange);
  onFilteredRowsChangeRef.current = onFilteredRowsChange;
  const filteredRowsSignature =
    isServerPaginated || !onFilteredRowsChange
      ? null
      : table.getFilteredRowModel().rows.map((r) => r.id).join(",");
  React.useEffect(() => {
    if (isServerPaginated || !onFilteredRowsChangeRef.current) return;
    onFilteredRowsChangeRef.current(
      table.getFilteredRowModel().rows.map((r) => r.original),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRowsSignature, isServerPaginated]);

  // Expose the APPLIED FILTER STATE (column-filter chip values + global search
  // term) so a page can stamp an export's provenance banner with exactly the
  // filters the operator has on screen (LANE A4). Opt-in; render-loop safe via a
  // JSON signature; callback read through a ref so its identity is not a dep.
  const onFilterStateChangeRef = React.useRef(onFilterStateChange);
  onFilterStateChangeRef.current = onFilterStateChange;
  const filterStateSignature = onFilterStateChange
    ? JSON.stringify({
        c: persistence.columnFilters,
        g: persistence.globalFilter ?? "",
      })
    : null;
  React.useEffect(() => {
    if (!onFilterStateChangeRef.current) return;
    onFilterStateChangeRef.current({
      columnFilters: persistence.columnFilters as {
        id: string;
        value: unknown;
      }[],
      globalFilter: persistence.globalFilter ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStateSignature]);

  const rawSelectedRowCount = Object.keys(rowSelection).length;
  const selectedItems = table
    .getFilteredSelectedRowModel()
    .rows.map((r) => r.original);

  // --- Select All Across Pages (Gmail-style) ---
  // When server-paginated, use the server's total count (not the client-side filtered count)
  const filteredRowCount = isServerPaginated
    ? (pagination?.total ?? data.length)
    : table.getFilteredRowModel().rows.length;
  const pageRowCount = table.getRowModel().rows.length;
  const allPageRowsSelected =
    pageRowCount > 0 && table.getIsAllPageRowsSelected();

  const totalColWidth = table.getCenterTotalSize();

  // --- Proportional column widths ("magic" CSS-only distribution) ---
  // Columns fill 100% of the container and share the width PROPORTIONALLY to
  // their defined `size`, so the first/large columns (Name) get more room and
  // narrow ones (Status badges) get less — while truly fixed columns (select,
  // actions, anything with enableResizing === false) keep an exact px width.
  // Flexible columns get a `width: N%` of the flexible budget; their `size`
  // becomes a `min-width` so the table scrolls horizontally instead of
  // crushing them when the viewport is narrow. Stable across populated/empty
  // states (no row-count-dependent slack redistribution → no shrink/snap),
  // and no filler column or width-measuring needed.
  const colWidthMeta = React.useMemo(() => {
    const leaf = table.getVisibleLeafColumns();
    const isFixed = (c: (typeof leaf)[number]) =>
      c.columnDef.enableResizing === false ||
      // pinned utility columns are always fixed
      c.id === "select" ||
      c.id === "actions";
    const flexCols = leaf.filter((c) => !isFixed(c));
    const flexSizeSum = flexCols.reduce((s, c) => s + (c.getSize() || 0), 0) || 1;
    const styleFor = (colId: string): React.CSSProperties => {
      const col = leaf.find((c) => c.id === colId);
      if (!col) return {};
      const size = col.getSize();
      if (isFixed(col)) {
        return { width: `${size}px`, minWidth: `${size}px`, maxWidth: `${size}px` };
      }
      const pct = (size / flexSizeSum) * 100;
      return {
        width: `${pct}%`,
        minWidth: `${col.columnDef.minSize || size}px`,
      };
    };
    return { styleFor };
  }, [table, persistence.columnSizing, persistence.columnVisibility]);

  // Synthetic "all across pages selected" flag — Gmail-style.
  // When server-paginated, we can't enumerate every row ID into rowSelection
  // without fetching every page. Instead, we flip this boolean and have bulk
  // actions send { selectAll: true, filters: activeFilters } to the backend,
  // which already supports that mode via BulkTargetBase.
  //
  // Controlled mode: when the parent owns the flag (passed via
  // externalAllAcrossPagesSelected), the table reflects parent state instead
  // of managing it locally. This keeps the synthetic flag alive across
  // remounts (wizard step navigation, route change, etc.) — same pattern
  // as externalRowSelection.
  const isAllAcrossPagesControlled =
    externalAllAcrossPagesSelected !== undefined;
  const [internalAllAcrossPagesSelected, setInternalAllAcrossPagesSelected] =
    React.useState(false);
  const isAllAcrossPagesSelected = isAllAcrossPagesControlled
    ? externalAllAcrossPagesSelected
    : internalAllAcrossPagesSelected;
  const setIsAllAcrossPagesSelected = React.useCallback(
    (value: boolean) => {
      if (isAllAcrossPagesControlled) {
        onExternalAllAcrossPagesSelectedChange?.(value);
        return;
      }
      setInternalAllAcrossPagesSelected(value);
    },
    [isAllAcrossPagesControlled, onExternalAllAcrossPagesSelectedChange],
  );

  // Bug #53 — when the synthetic "all across pages" flag is on (or the
  // client-side equivalent — every filtered row is selected), the footer
  // should report the true filtered total instead of the page-bounded
  // rowSelection keycount. The select-all banner and footer must agree or
  // users get conflicting signals ("All 5,000 selected" / "25 selected").
  const selectedRowCount = isAllAcrossPagesSelected
    ? filteredRowCount
    : rawSelectedRowCount;

  // When the filter / page changes (pagination?.page change, columnFilters
  // change, or rowSelection manually edited) the synthetic flag is stale.
  // Reset it whenever the selection becomes incomplete.
  //
  // CONTROLLED MODE: the parent owns the synthetic flag's lifecycle. It
  // also owns the per-page selection diff (excludeRecipientIds). When the
  // operator unchecks ONE row inside selectAll mode, the parent adds that
  // id to excludeRecipientIds while keeping selectAll=true (intentional —
  // selectAll-minus-N semantics). At that moment getIsAllPageRowsSelected
  // briefly returns false, but the parent's selectAll intent is unchanged.
  // The previous auto-reset would fire here and notify the parent
  // `setSelectAll(false)`, collapsing selectedRecipientIds (which was [] by
  // selectAll-mode invariant) to zero — the exact regression a 2026-05-22
  // critique pass caught. In controlled mode the parent IS the source of
  // truth; the table never writes the flag back via this effect.
  //
  // #253 — under SERVER pagination this effect was ALSO the thing that
  // silently destroyed the mode. Page 2's rows always arrive unselected, so
  // `allPageRowsSelected` goes false on every page turn for a reason that has
  // nothing to do with the operator's intent — and with `pagination?.page` in
  // the dependency list the flag was torn down the moment they paged forward
  // to check what they had selected. The banner reverted, the bulk bar
  // unmounted (its page-scope row model was empty) and any subsequent bulk
  // action ran against the 10 ids that happened to remain in `rowSelection`.
  // Server pages therefore get two narrower rules instead (below): an
  // explicit SHRINK of the selection ends the mode, and a FILTER change ends
  // the mode — a page turn does neither.
  React.useEffect(() => {
    if (isAllAcrossPagesControlled) return;
    if (isServerPaginated) return;
    if (!allPageRowsSelected) setIsAllAcrossPagesSelected(false);
  }, [
    allPageRowsSelected,
    pagination?.page,
    persistence.columnFilters,
    isAllAcrossPagesControlled,
    isServerPaginated,
    setIsAllAcrossPagesSelected,
  ]);

  // #253 (server pages, rule 1) — an explicit SHRINK of the selection ends
  // "all N results": unticking a row, or Clear selection. A page turn never
  // shrinks `rowSelection` (server pages are exempt from the prune effect,
  // and the materialise effect below only ever ADDS the new page's ids), so
  // this cannot fire for paging the way the old `allPageRowsSelected` test
  // did.
  //
  // #412 — this rule KEEPS its controlled-mode guard (rule 2 below no longer
  // needs one). A shrink is the one signal the table cannot read on the
  // parent's behalf: controlled consumers implement selectAll-MINUS-N, where
  // unticking a row inside the mode is a deliberate exclusion that leaves the
  // mode ON (IndividualsPanel moves the id into `excludeRecipientIds`, and its
  // derived `rowSelection` shrinks by exactly one). Ending the mode there is
  // the 2026-05-22 regression documented on the effect above: `selectAll`
  // flips false while `selectedRecipientIds` is [] by that mode's invariant,
  // so the whole selection collapses to zero. A filter change carries no such
  // ambiguity, which is why only rule 2 opens up.
  const prevRawSelectedRowCountRef = React.useRef(rawSelectedRowCount);
  React.useEffect(() => {
    const prev = prevRawSelectedRowCountRef.current;
    prevRawSelectedRowCountRef.current = rawSelectedRowCount;
    if (isAllAcrossPagesControlled || !isServerPaginated) return;
    if (rawSelectedRowCount < prev) setIsAllAcrossPagesSelected(false);
  }, [
    rawSelectedRowCount,
    isAllAcrossPagesControlled,
    isServerPaginated,
    setIsAllAcrossPagesSelected,
  ]);

  // #412 — ending the mode is not instantaneous when the flag is CONTROLLED:
  // it lives in the parent, so `isAllAcrossPagesSelected` still reads true for
  // the rest of this commit. Without this latch the materialise effect below
  // would re-tick the page we just cleared and hand the remnant straight back.
  // Dropped as soon as the parent's `false` arrives (or the operator opts back
  // in), so a consumer that ignores the callback is never left stuck.
  // #415 — WAI-ARIA grid keyboard pattern. Opt-in; see the prop's docblock.
  const gridTableRef = React.useRef<HTMLTableElement | null>(null);
  const gridKeyboard = useGridKeyboardNavigation(gridTableRef, {
    enabled: keyboardNavigation,
  });

  const allAcrossPagesEndingRef = React.useRef(false);

  // #253 (server pages, rule 2) — the "all N" the operator agreed to is
  // defined by the filter that was active when they clicked it, so a filter
  // or search change ends the mode. (Client pages already get this through
  // the prune effect shrinking `rowSelection`.)
  //
  // #412 — this used to open with `if (isAllAcrossPagesControlled || …)
  // return`, so it never ran for the tables that hold the flag in the parent
  // (campaign wizard IndividualsPanel, ESA monitoring, ER Search & Remediate,
  // PT users). The guard was copied from the `allPageRowsSelected` effect
  // above, where it IS needed — that one reads a signal (a page's rows going
  // unticked) the parent uses to mean something else. Nothing of the sort
  // applies here, and `setIsAllAcrossPagesSelected` already fans the change
  // out to the parent, so the mode now ends for controlled consumers too and
  // a bulk action can no longer dispatch `{ selectAll: true }` against a
  // filter the operator never agreed to.
  //
  // #412, second half — ending the mode also CLEARS the selection. In all-N
  // mode `rowSelection` is derived, not chosen: the materialise effect below
  // ticks whatever page is on screen so the checkboxes agree with the banner.
  // Left behind, those ids get intersected with the newly filtered page and
  // report against a selection that no longer exists ("9 of 10 selected items
  // are hidden by the current filters or page"). Only when the mode was
  // actually ON — an ordinary hand-picked selection must survive a filter
  // change, which is the whole point of the #210/#256 hidden-selection notice.
  // #469/#470 — ADVANCED SEARCH belongs in this signature too, and its absence
  // was the dangerous half. The rule above is "the all-N the operator agreed to
  // is defined by the filter that was active when they clicked it" — an
  // advanced-search condition is exactly such a filter, but changing one left
  // the mode ON, so a subsequent Archive/Delete dispatched
  // `{ selectAll: true }` against a set the operator had just narrowed away
  // from. Column filters and the global search already ended it; this did not.
  //
  // Included for CONTROLLED consumers as well as uncontrolled: even when the
  // server sees the search, changing it changes the set, so the agreed "all N"
  // is no longer the N in front of them.
  const selectionFilterSignature = JSON.stringify({
    c: persistence.columnFilters,
    g: persistence.globalFilter ?? "",
    a: advancedSearchState ?? null,
  });
  const prevSelectionFilterSignatureRef = React.useRef(
    selectionFilterSignature,
  );
  const selectionFilterSignatureSeededRef = React.useRef(false);
  React.useEffect(() => {
    // Hydration is not an operator action. `useTablePersistence` restores the
    // persisted column filters in the same commit that flips `mounted`, so
    // the first signature worth comparing against is the one AFTER that.
    // Without this seed a consumer that mounts with the flag already true —
    // a restored campaign draft does exactly that — would have its selection
    // torn down by its own localStorage before it rendered a second time.
    if (!persistence.mounted) return;
    const prev = prevSelectionFilterSignatureRef.current;
    prevSelectionFilterSignatureRef.current = selectionFilterSignature;
    if (!selectionFilterSignatureSeededRef.current) {
      selectionFilterSignatureSeededRef.current = true;
      return;
    }
    if (!isServerPaginated) return;
    if (prev === selectionFilterSignature) return;
    if (isAllAcrossPagesSelected) {
      // Selection first: a controlled parent that merges these two writes
      // from stale props must land on "mode off" rather than "mode off, page
      // still ticked".
      allAcrossPagesEndingRef.current = true;
      setRowSelection({});
      // Only ends the mode when it was actually ON. Calling this
      // unconditionally fires `onExternalAllAcrossPagesSelectedChange(false)`
      // at every filter change for controlled consumers that never turned the
      // mode on — and two of them treat that `false` as meaningful.
      setIsAllAcrossPagesSelected(false);
    }
  }, [
    selectionFilterSignature,
    persistence.mounted,
    isServerPaginated,
    isAllAcrossPagesSelected,
    setRowSelection,
    setIsAllAcrossPagesSelected,
  ]);

  // #253 — with the flag on, every row on whatever page the operator lands on
  // IS part of the selection, so tick them. Without this the new page's rows
  // render UNCHECKED under a banner that says all N are selected (the
  // reporter read this off the DOM), and `getFilteredSelectedRowModel()` —
  // what the bulk bar counts and what the toolbar's five dialogs measure — is
  // empty on that page. Only ever additive, so rule 1 above cannot misfire.
  React.useEffect(() => {
    if (!isServerPaginated) return;
    if (!isAllAcrossPagesSelected) {
      allAcrossPagesEndingRef.current = false;
      return;
    }
    // #412 — a filter change has already ended the mode; the parent's `false`
    // just hasn't landed yet. Re-ticking here would restore the exact stale
    // remnant rule 2 cleared.
    if (allAcrossPagesEndingRef.current) return;
    const pageRowIds = table.getRowModel().rows.map((r) => r.id);
    if (pageRowIds.length === 0) return;
    setRowSelection((prev) => {
      let changed = false;
      const next: RowSelectionState = { ...prev };
      for (const id of pageRowIds) {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllAcrossPagesSelected, isServerPaginated, data]);

  // Client-paginated case: treat "all filtered rows present in rowSelection"
  // as equivalent to the synthetic flag.
  const clientAllFilteredRowsSelected =
    !isServerPaginated &&
    filteredRowCount > 0 &&
    selectedRowCount === filteredRowCount;
  const allFilteredRowsSelected =
    isAllAcrossPagesSelected || clientAllFilteredRowsSelected;
  // #469/#470 — cross-page select is only meaningful when the SERVER can see
  // the same filter the operator is looking at.
  //
  // Advanced search runs client-side over the rows already fetched, and when it
  // is UNCONTROLLED nothing carries it to the server. `filteredRowCount` is then
  // `pagination.total` — the answer to a different question. Offering "Select
  // all 83" beside 2 matching rows handed a destructive bulk a set the operator
  // never saw: consumers turn the flag into `{ selectAll: true, filters }`, and
  // `filters` cannot express an advanced-search condition, so Archive/Delete ran
  // against all 83.
  //
  // Controlled consumers are unaffected: they own sending the state, so the
  // total IS the filtered total and the affordance means what it says. This is
  // the shared component precisely because 18 server-paginated tables across
  // phishing, announcements, LMS and recipients share the same exposure.
  //
  // Withholding the affordance costs a convenience. Offering it costs data.
  const crossPageSelectUnsafe =
    isServerPaginated &&
    !isAdvancedSearchControlled &&
    activeAdvancedSearchCount > 0;

  const showSelectAllBanner =
    enableRowSelection &&
    allPageRowsSelected &&
    !allFilteredRowsSelected &&
    !crossPageSelectUnsafe &&
    filteredRowCount > pageRowCount;
  const showAllSelectedBanner =
    enableRowSelection &&
    allFilteredRowsSelected &&
    filteredRowCount > pageRowCount;

  const selectAllFiltered = React.useCallback(() => {
    if (isServerPaginated) {
      // Just flip the synthetic flag. No fetch, no row enumeration.
      // The bulk action toolbar will pick this up via the `meta` arg and
      // dispatch filter-based bulk operations to the backend.
      allAcrossPagesEndingRef.current = false;
      setIsAllAcrossPagesSelected(true);
      return;
    }
    // Client-paginated: the full filtered set is already in memory, so
    // materialising rowSelection is cheap and correct.
    const newSelection: RowSelectionState = {};
    for (const row of table.getFilteredRowModel().rows) {
      newSelection[row.id] = true;
    }
    setRowSelection(newSelection);
  }, [isServerPaginated, table, setRowSelection, setIsAllAcrossPagesSelected]);

  // --- Column Drag End ---
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const allPinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
    const ids = table
      .getAllLeafColumns()
      .filter((c) => !allPinned.includes(c.id))
      .map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ids, oldIndex, newIndex);
    persistence.setColumnOrder([
      ...persistence.pinnedLeft,
      ...reordered,
      ...persistence.pinnedRight,
    ]);
  };

  // --- Filter Drag End ---
  const handleFilterDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = persistence.filterOrder.findIndex(
        (id) => id === active.id,
      );
      const newIndex = persistence.filterOrder.findIndex(
        (id) => id === over?.id,
      );
      if (oldIndex !== -1 && newIndex !== -1) {
        persistence.setFilterOrder(
          arrayMove(persistence.filterOrder, oldIndex, newIndex),
        );
      }
    }
  };

  // --- Header Drag-and-Drop (direct on table headers) ---
  const [activeHeaderId, setActiveHeaderId] = React.useState<string | null>(
    null,
  );

  const handleHeaderDragStart = (event: DragStartEvent) => {
    setActiveHeaderId(String(event.active.id));
  };

  const handleHeaderDragEnd = (event: DragEndEvent) => {
    setActiveHeaderId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const pinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
    const ids = table
      .getAllLeafColumns()
      .filter((c) => !pinned.includes(c.id))
      .map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(ids, oldIndex, newIndex);
    persistence.setColumnOrder([
      ...persistence.pinnedLeft,
      ...reordered,
      ...persistence.pinnedRight,
    ]);
  };

  const handleHeaderDragCancel = () => {
    setActiveHeaderId(null);
  };

  // --- Column display name helper ---
  const getDisplayName = (columnId: string): string => {
    return (
      columnDisplayNames[columnId] ||
      columnId.charAt(0).toUpperCase() + columnId.slice(1)
    );
  };

  // --- Lookup map for custom filter configs (id -> render fn) ---
  const customFilterMap = React.useMemo(() => {
    const map = new Map<string, CustomFilterConfig>();
    for (const cf of customFilters ?? []) map.set(cf.id, cf);
    return map;
  }, [customFilters]);

  // --- Column and filter drag for screen readers ---
  // dnd-kit's own instructions and announcements are English and speak raw
  // ids ("Picked up draggable item firstName"). These use the interface
  // language and the names on screen. The names are read through a ref when a
  // drag is announced, so the objects handed to DndContext stay stable.
  const dragNamesRef = React.useRef({
    column: (id: string) => id,
    filter: (id: string) => id,
  });
  dragNamesRef.current = {
    column: getDisplayName,
    filter: (id: string) =>
      filters?.find((f) => f.id === id)?.title ??
      customFilterMap.get(id)?.label ??
      dateFilters?.find((d) => d.key === id)?.label ??
      id,
  };
  const columnDragAccessibility = React.useMemo(
    () => dragAccessibility(t, (id) => dragNamesRef.current.column(id)),
    [t],
  );
  const filterDragAccessibility = React.useMemo(
    () => dragAccessibility(t, (id) => dragNamesRef.current.filter(id)),
    [t],
  );

  // --- Draggable columns (exclude pinned) ---
  const allPinned = [...persistence.pinnedLeft, ...persistence.pinnedRight];
  const draggableColumns = table
    .getAllLeafColumns()
    .filter((c) => !allPinned.includes(c.id));
  const draggableColumnIds = draggableColumns.map((c) => c.id);

  // --- Has any filter chips visible (column-bound OR custom) ---
  const hasVisibleFilters =
    (filters || customFilters) &&
    persistence.filterOrder.some((id) => persistence.filterVisibility[id]);

  // --- Active filter/search detection (for the filtered-empty no-results
  //     state — "data exists but the current filters match nothing"). ---
  const hasActiveTableFilters =
    // #411 — a filter the PARENT owns and sends to the server itself is not in
    // any of the table-internal signals below. Without this, a
    // filtered-to-empty list renders the "no data" wording and claims the
    // workspace is empty.
    hasExternalFilters ||
    persistence.columnFilters.length > 0 ||
    (searchInputValue?.trim().length ?? 0) > 0 ||
    // Include the PERSISTED global filter, not just the live input. On a fresh
    // mount the persistence hook loads the persisted search term into
    // `persistence.globalFilter` AFTER the initial render, while
    // `searchInputValue` may still be "" — so a reloaded search must be
    // detected here too, otherwise a filtered-to-empty list is mistaken for a
    // truly-empty one (prod 2026-06-26: a persisted "Search groups" term hid a
    // real group behind the "Create your first group" empty state).
    (persistence.globalFilter?.trim().length ?? 0) > 0 ||
    activeAdvancedSearchCount > 0 ||
    customFiltersActive === true;

  // --- #415 (c) a11y: polite result-count announcement --------------------
  // A sighted operator sees the row count change the instant a search or a
  // filter lands; a screen-reader user got nothing at all. Mirror the number
  // the footer prints into a polite live region.
  //
  // NOT INERT ON SERVER-PAGINATED TABLES — the trap this was written around.
  // There the count (`pagination.total`) arrives one or more renders AFTER the
  // filter signature changes, so announcing on the signature alone would read
  // out the PREVIOUS total. Two things prevent that: the key carries the count
  // as well as the filter state, and while `isFetching` is true the baseline is
  // deliberately left stale so the announcement fires when the new number
  // lands, not on the old one.
  const resultAnnouncementKey = JSON.stringify({
    // Recompute an existing announcement in the newly selected language.
    locale,
    g: persistence.globalFilter ?? "",
    c: persistence.columnFilters,
    a: activeAdvancedSearchCount,
    x: customFiltersActive === true,
    n: filteredRowCount,
  });
  // `seq` is not decoration. Two different searches can produce the SAME
  // sentence ("0 recipients match…" → "0 recipients match…"). React bails out
  // of a setState to an identical string, the text node never mutates, and a
  // live region that does not mutate is never announced — the second search
  // would be silent. Keying the span on `seq` replaces the node instead, and a
  // node ADDED to a live region is the mutation assistive tech announces most
  // reliably.
  const [resultAnnouncement, setResultAnnouncement] = React.useState({
    text: "",
    seq: 0,
  });
  // Read through refs so the wording is always computed from the CURRENT
  // render's numbers without becoming an effect dependency (which would
  // re-announce on unrelated re-renders).
  const announcementTextRef = React.useRef("");
  announcementTextRef.current = hasActiveTableFilters
    ? nounIsReadable
      ? t("shell.table.matchingWithNoun", { count: filteredRowCount, noun: entityNoun, singular: singularNoun })
      : t("shell.table.matching", { count: filteredRowCount })
    : nounIsReadable
      ? t("shell.table.showingAllWithNoun", { count: filteredRowCount, noun: entityNoun, singular: singularNoun })
      : t("shell.table.showingAll", { count: filteredRowCount });
  const announcedKeyRef = React.useRef<string | null>(null);
  const announcementSeqRef = React.useRef(0);
  React.useEffect(() => {
    // Mid-flight: the number on screen is the OLD one. Do not bank the key —
    // the comparison below must still see a change when the data lands.
    if (isFetching) return;
    if (announcedKeyRef.current === null) {
      // First settled render is not a change; announcing it would talk over
      // the page the operator just navigated to.
      announcedKeyRef.current = resultAnnouncementKey;
      return;
    }
    if (announcedKeyRef.current === resultAnnouncementKey) return;
    announcedKeyRef.current = resultAnnouncementKey;
    announcementSeqRef.current += 1;
    setResultAnnouncement({
      text: announcementTextRef.current,
      seq: announcementSeqRef.current,
    });
  }, [resultAnnouncementKey, isFetching]);

  // --- #415 (b) a11y: is there a footer worth skipping to? -----------------
  // The client footer renders on `!pagination && clientPagination`; the
  // server/cursor footer on `pagination || renderFooterStatus`. A skip link
  // with no landing place is worse than none, so it is gated on the union.
  const hasPaginationFooter = Boolean(
    pagination || renderFooterStatus || clientPagination,
  );

  // Reset the in-table filters + search + advanced search so the operator can
  // recover from a filtered-empty result. Server-paginated callers also get
  // their onSearchChange("") + onFilterChange([]) pinged so the server query
  // re-widens.
  const clearAllTableFilters = React.useCallback(() => {
    persistence.setColumnFilters([]);
    persistence.setGlobalFilter("");
    setSearchInputValue("");
    if (onSearchChange) onSearchChange("");
    if (onFilterChange) onFilterChange([]);
    // Reset consumer-owned custom filters (e.g. the phishing Activity report's
    // Bot/Honeypot/Transport/IP dropdowns) too — otherwise a table filtered
    // only via custom chips stays stuck (no built-in filter to clear).
    onClearCustomFilters?.();
    // Advanced search has no top-level clear, so reset it through the SAME
    // setter the table READS from: `setAdvancedSearchState` resolves to the
    // consumer's `onStateChange` in controlled mode and to the internal
    // `useTablePersistence` store otherwise.
    //
    // #358 / #359 (one bug, filed twice): this used to be gated on
    // `advancedSearch?.onStateChange`, which only EXISTS in controlled mode.
    // Every uncontrolled table therefore kept its advanced search after
    // "Clear all" — chips and search box cleared, rows still filtered — and
    // the persisted `<prefix>-advanced-search` key replayed the same
    // conditions on the next mount. Must stay AFTER onClearCustomFilters (see
    // lib/__tests__/clear-all-fires-custom-filters-last.guard.spec.ts).
    if (advancedSearch) {
      setAdvancedSearchState(advancedSearch.emptyState);
    }
    try {
      localStorage.removeItem(`${localStoragePrefix}-column-filters`);
    } catch {
      /* ignore */
    }
  }, [
    persistence,
    onSearchChange,
    onFilterChange,
    onClearCustomFilters,
    advancedSearch,
    setAdvancedSearchState,
    localStoragePrefix,
  ]);

  // --- Empty State: "replace" mode ---
  // Rendered INSIDE the baked card so an empty table still shows the framed,
  // full-height card (not bare content on the page background). The content is
  // centered vertically + horizontally within that card.
  //
  // CRITICAL: only do the full-table replace when the list is GENUINELY empty
  // (no search/filter active). When a search or filter is what emptied the
  // list, the full replace would hide the toolbar+search and falsely claim the
  // data doesn't exist ("Create your first group") — the exact prod bug on
  // 2026-06-26 where a persisted "Search groups" term made a real group look
  // missing. With filters active we fall through to the normal render, which
  // keeps the search box visible and shows the "No results match your filters
  // — Clear filters" state (see the no-results overlay below).
  if (
    !errorState &&
    emptyState?.mode === "replace" &&
    data.length === 0 &&
    !hasActiveTableFilters &&
    !isFetching
  ) {
    return (
      <div
        className={cn(
          "w-full flex flex-col",
          // Match the <Card> component chrome so a UDT-with-card and a
          // page-level <Card>-wrapping a UDT (card={false}) render the
          // same: rounded-xl + subtle shadow (2026-06-17 design alignment).
          card && "rounded-xl border bg-card shadow-sm overflow-hidden",
          card && (fillHeight ? "flex-1 min-h-0" : "min-h-[600px]"),
          // Embedded (card=false): still give the empty state a real floor so
          // it centers instead of collapsing into a stray right-aligned row.
          !card && (fillHeight ? "flex-1 min-h-[300px]" : "min-h-[300px]"),
        )}
      >
        <div className="flex flex-1 flex-col items-center justify-center py-16 px-4 text-center">
          {emptyState.icon}
          <h3 className="text-lg font-semibold mt-4">{emptyState.title}</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            {emptyState.description}
          </p>
          {emptyState.actions && <div className="mt-6">{emptyState.actions}</div>}
        </div>
      </div>
    );
  }

  // --- Render ---
  return (
    <div
      // #204/#230 — reserve clearance under the fixed Feedback FAB so it never
      // covers this card's sticky pagination footer / Actions column. Only
      // standalone card tables reach the viewport's bottom corners; embedded
      // (card=false) tables live inside dialogs/widgets. globals.css pads the
      // footer on the side body[data-fab-reserve] names ("right" | "left"),
      // i.e. while the FAB rests at the inline-end bottom corner (bottom-right
      // in English, bottom-left in Arabic), and for the default corner while
      // the server's empty value stands.
      {...(card ? { "data-fab-safe-area": "" } : {})}
      className={cn(
        "w-full",
        // Baked-in card chrome — single source of truth for table framing.
        // Callers must NOT add their own <Card> wrapper. The card always
        // gives border + bg + a flex column. Height behaviour:
        //  • fillHeight → flex-1 min-h-0 so the card fills leftover viewport
        //    and the footer stays on screen. A 600px floor overflowed
        //    PageShell (Courses/Assignments/Enrollments clipped pagination).
        //  • otherwise → fixed min-h-[600px] (legacy, non-filling callers).
        // Match the <Card> component chrome (rounded-xl + shadow-sm) so all
        // tables look the same whether they use UDT's built-in card or get
        // wrapped in a page-level <Card> (card={false}). 2026-06-17.
        card && "rounded-xl border bg-card shadow-sm flex flex-col overflow-hidden isolate",
        card && (fillHeight ? "flex-1 min-h-0" : "min-h-[600px]"),
        // Non-card fillHeight (embedded/legacy) keeps its original behaviour.
        !card && fillHeight && "flex h-full flex-col min-h-0",
      )}
    >
      {/* #415 (b) — skip link. One row holds 19 tabbable controls and the
          header 24, so reaching the pagination controls below a default
          14-row table costs ~296 Tab presses (~1,900 at 100 rows/page).
          First focusable element in the table, visually hidden until focused
          (same sr-only + focus:not-sr-only pattern as the employee-portal
          skip link). The wrapper is `relative` and zero-height — sr-only is
          position:absolute, so it contributes no layout — which gives the
          focused state something to anchor to inside the card instead of the
          page. The click handler moves focus explicitly rather than relying on
          fragment navigation alone. */}
      {hasPaginationFooter && (
        <div className="relative">
          <a
            href={`#${paginationRegionId}`}
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:rounded-md focus:bg-foreground focus:px-3 focus:py-1.5 focus:text-xs focus:font-medium focus:text-background focus:shadow-lg"
            onClick={(e) => {
              const target = document.getElementById(paginationRegionId);
              if (!target) return;
              e.preventDefault();
              target.focus();
            }}
          >
            {t("shell.table.skipToPagination")}
          </a>
        </div>
      )}

      {/* #415 (c) — result-count live region. Mounted ALWAYS (empty at first),
          because a live region a screen reader has not been watching since
          before the change does not get announced when it appears. Named so it
          is distinguishable from dnd-kit's own assertive drag-announcement
          region, which is also role="status". */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={t("shell.table.resultsRegion")}
        className="sr-only"
      >
        {resultAnnouncement.text ? (
          <span key={resultAnnouncement.seq}>{resultAnnouncement.text}</span>
        ) : null}
      </div>

      {/* Toolbar — px-4 keeps horizontal breathing room; py-2 trims the
          top/bottom padding so the search row sits closer to the table
          header (2026-05-06 audit). */}
      <div className="flex flex-col gap-2 p-4 border-b">
        {/* Row 1: Search + actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {renderToolbarLeft?.(table)}

            {/* Search */}
            {enableSearch && (
              <div className="flex flex-col gap-0.5">
                <div className="relative">
                  <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder ?? t("shell.table.searchPlaceholder")}
                    value={searchInputValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="h-9 w-50 lg:w-70 ps-8 pe-8"
                  />
                  {searchInputValue && (
                    <button
                      type="button"
                      title={t("shell.table.clearSearch")}
                      onClick={() => {
                        setSearchInputValue("");
                        persistence.setGlobalFilter("");
                        if (onSearchChange) onSearchChange("");
                      }}
                      className="absolute end-2 top-2.5 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {searchHintText && (
                  <p className="text-xs text-muted-foreground ps-1">{searchHintText}</p>
                )}
              </div>
            )}

            {/* Advanced search trigger */}
            {advancedSearch?.renderTrigger({
              onClick: () => setAdvancedSearchOpen(true),
              activeCount: activeAdvancedSearchCount,
            })}

            {/* Filter chips — ALWAYS inline with search (the row is flex-wrap
                so they flow onto a new line only when space runs out, saving
                vertical space). The legacy `below` placement now renders here
                too; the separate Row 2 below is disabled. */}
            {hasVisibleFilters && (
              <>
                {persistence.filterOrder.map((filterId) => {
                  const custom = customFilterMap.get(filterId);
                  // A custom filter that currently HOLDS a value force-shows in
                  // the toolbar even if the user hid it — so an active filter
                  // (e.g. a stat-card-applied metric) stays visible and
                  // understandable. Inactive filters still respect the toggle.
                  const forceShow = custom?.active === true;
                  if (!persistence.filterVisibility[filterId] && !forceShow) {
                    return null;
                  }
                  const colConfig = filters?.find((f) => f.id === filterId);
                  if (colConfig) {
                    return (
                      <MultiSelectFilter
                        key={filterId}
                        title={colConfig.title}
                        options={colConfig.options}
                        selectedValues={
                          (table
                            .getColumn(colConfig.column)
                            ?.getFilterValue() as string[]) ?? []
                        }
                        onSelectionChange={(values) =>
                          table
                            .getColumn(colConfig.column)
                            ?.setFilterValue(
                              toColumnFilterValue(values, colConfig.options),
                            )
                        }
                        hideSearchMode={colConfig.hideSearchMode}
                        emptyOptionHint={colConfig.emptyOptionHint}
                      />
                    );
                  }
                  if (custom) {
                    return (
                      <React.Fragment key={filterId}>
                        {custom.render()}
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
              </>
            )}

            {/* Independent date-range filter chips (inline placement).
                Each chip is its own compact popover; consumer owns state.
                Gated by persistence.filterVisibility[df.key] so the
                Filters dropdown can toggle them on/off like every other
                filter. */}
            {dateFilters?.map((df) =>
                persistence.filterVisibility[df.key] === false ? null : (
                  <DateFieldFilter
                    key={df.key}
                    label={df.label}
                    value={df.value}
                    onChange={df.onChange}
                    presets={df.presets}
                    disableFutureDates={df.disableFutureDates}
                  />
                ),
              )}

            {/* Clear all — placed AFTER every chip (regular + date) so it
                consistently sits at the end of the chip flow regardless of
                wrap order. Previously rendered between the regular and date
                chips, which made it appear "sandwiched" in the middle of the
                toolbar (P2 placement fix 2026-06-12).

                Clears search input + global filter + column filters + fires
                onSearchChange("") + onFilterChange([]) so server-paginated
                tables also re-widen, not just the visible chips.

                Gated on ANY active filter (column, global filter,
                search, or advanced search) — so it appears even when the user
                has typed a search query but hasn't picked a chip yet, which is
                the more common "I want to undo my filtering" case.

                `activeAdvancedSearchCount` belongs in that list: an advanced
                search narrows the rows without producing a chip, a global
                filter or search text, so a table filtered ONLY that way used
                to offer nothing to clear (#358 / #359). */}
            {(persistence.columnFilters.length > 0 ||
              persistence.globalFilter ||
              searchInputValue ||
              activeAdvancedSearchCount > 0 ||
              customFiltersActive) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={clearAllTableFilters}
              >
                <X className="h-3 w-3 me-1" />
                {t("common.clearAll")}
              </Button>
            )}

            {renderToolbarCenter?.(table)}
          </div>

          <div className="flex items-center gap-2">
            {renderToolbarRight?.(table, {
              allFilteredSelected: allFilteredRowsSelected,
              filteredTotal: filteredRowCount,
            })}

            {/* Density toggle — cycles compact ↔ default ↔ comfortable.
                Persists in localStorage. Tooltip shows the current mode. */}
            {enableDensityToggle && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={cycleDensity}
                title={t("shell.table.density", { density })}
                aria-label={t("shell.table.density", { density })}
              >
                <Rows3 className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}

            {/* Saved Views menu (opt-in via savedViewsKey).
                Snapshots the full persisted-state bundle and re-applies it
                on selection. Pure FE — localStorage namespace
                `<savedViewsKey>:saved-views`. */}
            {savedViewsKey && (
              <SavedViewsMenu
                storageKey={savedViewsKey}
                currentView={{
                  columnVisibility: persistence.columnVisibility,
                  columnOrder: persistence.columnOrder,
                  columnSizing: persistence.columnSizing,
                  sorting: persistence.sorting,
                  columnFilters: persistence.columnFilters,
                  globalFilter: persistence.globalFilter,
                  filterVisibility: persistence.filterVisibility,
                  filterOrder: persistence.filterOrder,
                }}
                onApplyView={(v: ViewState) => {
                  if (v.columnVisibility)
                    persistence.setColumnVisibility(v.columnVisibility);
                  if (v.columnOrder) persistence.setColumnOrder(v.columnOrder);
                  if (v.columnSizing) persistence.setColumnSizing(v.columnSizing);
                  if (v.sorting) persistence.setSorting(v.sorting);
                  if (v.columnFilters)
                    persistence.setColumnFilters(v.columnFilters);
                  if (typeof v.globalFilter === "string")
                    persistence.setGlobalFilter(v.globalFilter);
                  if (v.filterVisibility)
                    persistence.setFilterVisibility(v.filterVisibility);
                  if (v.filterOrder) persistence.setFilterOrder(v.filterOrder);
                }}
              />
            )}

            {/* Columns Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("shell.table.toggleColumns")}>
                  <Columns3 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-55">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t("shell.table.columns")}
                  </span>
                  <button
                    onClick={persistence.resetColumns}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  >
                    <RotateCcw className="h-2.5 w-2.5" />
                    {t("shell.table.reset")}
                  </button>
                </div>
                {/* FIX R1-m19 — drag affordance hint */}
                <p className="text-[10px] text-muted-foreground px-2 pb-1.5">
                  {t("shell.table.dragColumnsHint")}
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  accessibility={columnDragAccessibility}
                >
                  <SortableContext
                    items={draggableColumnIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {draggableColumns.map((column) => (
                      <SortableColumnItem
                        key={column.id}
                        column={column}
                        displayName={getDisplayName(column.id)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-xs text-muted-foreground justify-center gap-1.5"
                  onSelect={persistence.resetColumns}
                >
                  <RotateCcw className="h-3 w-3" />
                  {t("shell.table.resetToDefaults")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Filters Dropdown — lists both column-bound + custom filters */}
            {((filters && filters.length > 0) ||
              (customFilters && customFilters.length > 0)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label={t("shell.table.customizeFilters")}>
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-45">
                  {/* FIX R1-m20 — "Active filters: X of Y" summary */}
                  {(() => {
                    const totalFilterCount =
                      (filters?.length ?? 0) +
                      (customFilters?.length ?? 0) +
                      (dateFilters?.length ?? 0)
                    const activeFilterCount = persistence.filterOrder.filter(id => persistence.filterVisibility[id]).length
                    return (
                      <div className="px-2 py-1.5 flex items-center justify-between">
                        <span className="text-xs font-semibold text-muted-foreground">{t("shell.table.filters")}</span>
                        {totalFilterCount > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            {t("shell.table.activeFilters", { active: activeFilterCount, total: totalFilterCount })}
                          </span>
                        )}
                      </div>
                    )
                  })()}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleFilterDragEnd}
                    accessibility={filterDragAccessibility}
                  >
                    <SortableContext
                      items={persistence.filterOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      {persistence.filterOrder.map((filterId) => {
                        const colConfig = filters?.find(
                          (f) => f.id === filterId,
                        );
                        const custom = customFilterMap.get(filterId);
                        const dateConfig = dateFilters?.find(
                          (d) => d.key === filterId,
                        );
                        const displayName =
                          colConfig?.title ??
                          custom?.label ??
                          dateConfig?.label ??
                          null;
                        if (!displayName) return null;
                        return (
                          <SortableFilterItem
                            key={filterId}
                            filterId={filterId}
                            displayName={displayName}
                            checked={
                              persistence.filterVisibility[filterId] ?? false
                            }
                            onCheckedChange={(value) =>
                              persistence.setFilterVisibility((prev) => ({
                                ...prev,
                                [filterId]: !!value,
                              }))
                            }
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-muted-foreground justify-center gap-1.5"
                    onSelect={persistence.resetFilters}
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t("shell.table.resetToDefaults")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

      </div>

      {/* Bulk Actions */}
      {selectedRowCount > 0 &&
        renderBulkActions &&
        renderBulkActions(table, selectedItems, clearSelection, {
          allFilteredSelected: allFilteredRowsSelected,
          filteredTotal: filteredRowCount,
        })}
      {selectedRowCount > 0 &&
        !renderBulkActions &&
        bulkActions &&
        bulkActions.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b">
            <span className="text-sm text-muted-foreground">
              {t("shell.table.rowsSelected", { count: selectedRowCount })}
            </span>
            <div className="flex items-center gap-1 ms-2">
              {bulkActions.map((action) => {
                const buttonProps = {
                  variant: action.variant ?? "outline",
                  size: "sm" as const,
                  className: "h-8",
                  onClick: () => {
                    if (action.confirm) {
                      setConfirmAction(action);
                    } else {
                      action.onExecute(selectedItems, clearSelection, {
                        allFilteredSelected: allFilteredRowsSelected,
                        filteredTotal: filteredRowCount,
                      });
                    }
                  },
                  children: (
                    <>
                      {action.icon}
                      <span className={action.icon ? "ms-1" : ""}>
                        {action.label}
                      </span>
                    </>
                  ),
                };
                // A role without the action's grant sees it disabled with the
                // reason (fail-open while the grants load); no grant named → as before.
                return action.permission ? (
                  <PermissionButton
                    key={action.id}
                    action={action.permission.action}
                    subject={action.permission.subject}
                    {...buttonProps}
                  />
                ) : (
                  <Button key={action.id} {...buttonProps} />
                );
              })}
            </div>
          </div>
        )}

      {/* #210 — Hidden-selection notice. When a filter or page change hides
          EVERY currently-selected row, the bulk-action bar (which counts only
          rows present in the current view) silently vanishes even though the
          selection still exists on the server. Explain that instead of letting
          the bar disappear with no trace, and offer a one-click Clear. Skipped
          in "all across pages" mode (the count there is synthetic). */}
      {/* #256 — this used to fire ONLY when every selected row was hidden
          (`selectedItems.length === 0`). With 3 selected and 1 visible it
          stayed silent while the bulk action ran against the 1 visible id, so
          the partial case — the one where the operator has no way to notice —
          was the only case not explained. Gate on the intersection being
          SMALLER than the selection instead. */}
      {enableRowSelection &&
        !isAllAcrossPagesSelected &&
        rawSelectedRowCount > 0 &&
        selectedItems.length < rawSelectedRowCount && (
          <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b text-sm">
            <span className="text-muted-foreground">
              {selectedItems.length > 0
                ? nounIsReadable
                  ? t("shell.table.hiddenSomeWithNoun", {
                      hidden: rawSelectedRowCount - selectedItems.length,
                      total: rawSelectedRowCount,
                      noun: entityNoun,
                    })
                  : t("shell.table.hiddenSome", {
                      hidden: rawSelectedRowCount - selectedItems.length,
                      total: rawSelectedRowCount,
                    })
                : nounIsReadable
                  ? t("shell.table.hiddenAllWithNoun", {
                      count: rawSelectedRowCount,
                      noun: entityNoun,
                      // English grammar for English text: the page passes a
                      // plural noun, and one row reads "1 selected recipient".
                      singular: singularNoun,
                    })
                  : t("shell.table.hiddenAll", { count: rawSelectedRowCount })}
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-primary underline underline-offset-2 font-semibold cursor-pointer hover:text-primary/80"
            >
              {t("shell.table.clearSelection")}
            </button>
          </div>
        )}

      {/* Select All Across Pages Banner (Gmail-style) */}
      {showSelectAllBanner && (
        <div className="flex items-center gap-1 px-4 py-2 bg-primary/5 border-b text-sm">
          <span className="text-muted-foreground animate-select-all-attention">
            {t("shell.table.pageSelected", { count: pageRowCount })}
          </span>
          <button
            type="button"
            onClick={selectAllFiltered}
            className="text-primary underline underline-offset-2 font-semibold cursor-pointer hover:text-primary/80 animate-select-all-attention"
          >
            {t("shell.table.selectAllResults", { count: filteredRowCount })}
          </button>
        </div>
      )}
      {showAllSelectedBanner && (
        <div className="flex items-center gap-1 px-4 py-2 bg-primary/5 border-b text-sm">
          <span className="text-muted-foreground">
            {t("shell.table.allResultsSelected", { count: filteredRowCount })}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-primary underline underline-offset-2 font-semibold cursor-pointer hover:text-primary/80"
          >
            {t("shell.table.clearSelection")}
          </button>
        </div>
      )}

      {/* Table */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleHeaderDragStart}
        onDragEnd={handleHeaderDragEnd}
        onDragCancel={handleHeaderDragCancel}
        accessibility={columnDragAccessibility}
      >
        <div
          className={cn(
            "overflow-auto relative",
            densityClass,
            fillHeight && "flex-1 min-h-0",
            // Floating-scrollbar fix (2026-09-23): a horizontal scrollbar is
            // drawn at the bottom of THIS element, so in the 600px card with a
            // few rows it floated under the last row while the pager sat far
            // below (reported on /phishing/campaigns/[id]; audit found ~40 such
            // tables). `grow` = flex-grow only: the basis stays `auto`, so the
            // body starts at its rows' height and takes only POSITIVE spare
            // space. It never changes the CARD's size — only where the card's
            // empty space sits (inside the body instead of beside the pager).
            //
            // This is NOT #587's `flex-1 min-h-0` (zero basis, floor removed —
            // what let Courses/Enrollments collapse to 0px). With no spare space
            // (many rows, or a squeezed card) `grow` does nothing at all.
            card && !fillHeight && "grow",
          )}
          style={
            // fillHeight: NO inline minHeight. An inline `min-height` beats the
            // `min-h-0` class above, so a 12rem floor stopped this body from
            // shrinking to the space the card actually had. Measured on
            // /recipients: card 398px, children 157 toolbar + 192 body + 69
            // footer = 418px, so the card's `overflow-hidden` clipped ~21px off
            // the pagination bar even with the FAB parked elsewhere. `flex-1
            // min-h-0` alone gives this body exactly the leftover height, which
            // is the only value that cannot overflow its own card.
            //
            // The floor came from #587 ("Courses/Enrollments cannot go 0px").
            // That guard belongs on the CARD, not here: a card floor makes the
            // PAGE scroll, while a body floor makes the card CLIP — and a
            // clipped pagination bar is unusable rather than merely cramped.
            fillHeight
              ? undefined
              : card
                ? undefined
                : { maxHeight, minHeight }
          }
        >
          {isFetching && (
            <div className="absolute inset-0 z-10 bg-background/50 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
              <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {/* Bare <table> (NOT the <Table> primitive) — the primitive wraps the
              table in its own `overflow-x-auto` container div, which becomes a
              nested scroll context that traps position:sticky and stops the
              header from freezing against THIS overflow-auto body. Rendering
              the table directly here keeps the sticky <th> anchored to the
              scrolling body so the header freezes on vertical scroll. */}
          <table
            data-slot="table"
            ref={gridTableRef}
            {...(keyboardNavigation
              ? {
                  role: "grid",
                  onKeyDown: gridKeyboard.onKeyDown,
                  onFocusCapture: gridKeyboard.onFocusCapture,
                }
              : {})}
            className="caption-bottom text-sm table-fixed"
            style={{
              // Always 100% — flexible columns take a % share (proportional to
              // their `size`) and fixed columns take px, so the table fills the
              // container with the first column widest and Actions narrow.
              // `minWidth` on the table = total min so it scrolls when too narrow.
              width: "100%",
              minWidth: `${totalColWidth}px`,
            }}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                const visibleNonPinnedIds = headerGroup.headers
                  .filter((h) => !allPinned.includes(h.column.id))
                  .map((h) => h.column.id);

                return (
                  <SortableContext
                    key={headerGroup.id}
                    items={visibleNonPinnedIds}
                    // The header row runs right to left in Arabic; dnd-kit's
                    // stock strategy would slide the neighbours the wrong way.
                    strategy={horizontalListSortingStrategyFor(textDirection)}
                  >
                    <TableRow>
                      {headerGroup.headers.map((header) => {
                        const isPinned = allPinned.includes(header.column.id);

                        if (isPinned) {
                          const pinnedSortDir = header.column.getCanSort() ? header.column.getIsSorted() : undefined
                          const pinnedAriaSort: React.AriaAttributes["aria-sort"] =
                            pinnedSortDir === "asc"
                              ? "ascending"
                              : pinnedSortDir === "desc"
                                ? "descending"
                                : pinnedSortDir === false
                                  ? "none"
                                  : undefined
                          return (
                            <TableHead
                              key={header.id}
                              aria-sort={pinnedAriaSort}
                              className="h-11 px-2 first:ps-4 last:pe-4 sticky top-0 z-20 bg-background align-middle text-start overflow-hidden [&_button]:whitespace-normal [&_button]:text-start"
                              style={colWidthMeta.styleFor(header.column.id)}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                              {header.column.getCanResize() && (
                                <div
                                  onDoubleClick={() =>
                                    header.column.resetSize()
                                  }
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    header.getResizeHandler()(e);
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    header.getResizeHandler()(e);
                                  }}
                                  className="absolute end-0 top-0 h-full w-4 cursor-col-resize select-none touch-none flex items-center justify-center bg-transparent hover:bg-blue-500/10 z-10"
                                  style={{ userSelect: "none" }}
                                >
                                  <div
                                    className={`w-px h-[70%] border-e border-border hover:border-blue-500 ${
                                      header.column.getIsResizing()
                                        ? "border-blue-500 border-e-2"
                                        : ""
                                    }`}
                                  />
                                </div>
                              )}
                            </TableHead>
                          );
                        }

                        return (
                          <SortableTableHead
                            key={header.id}
                            headerId={header.column.id}
                            widthStyle={colWidthMeta.styleFor(header.column.id)}
                            width={header.getSize()}
                            minWidth={header.column.columnDef.minSize || 50}
                            maxWidth={header.column.columnDef.maxSize || 1000}
                            canResize={header.column.getCanResize()}
                            isResizing={header.column.getIsResizing()}
                            resizeHandler={header.getResizeHandler()}
                            resetSize={() => header.column.resetSize()}
                            ariaSortDir={header.column.getCanSort() ? header.column.getIsSorted() : undefined}
                          >
                            {header.isPlaceholder ? null : (
                              <>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                                {/* #415 (a) — DISTINCT ACCESSIBLE NAMES.
                                    SortableTableHead wraps these children in
                                    dnd-kit's activator, which useSortable()
                                    gives role="button" tabindex="0". Its
                                    accessible name is computed from its
                                    contents — i.e. the very same label as the
                                    real sort <button> nested inside it — so a
                                    screen reader read "Name, button, Name,
                                    button, Email, button, Email, button…":
                                    24 stops for 12 labels, with nothing saying
                                    which one sorts and which one reorders.

                                    This span is a SIBLING of the sort button,
                                    so it joins the activator's name and NOT the
                                    button's, splitting the two apart. sr-only
                                    is position:absolute, so it adds no layout
                                    inside the header's flex row.

                                    The activator KEEPS its tab stop on purpose:
                                    dnd-kit's KeyboardSensor is wired above with
                                    sortableKeyboardCoordinates, so that stop IS
                                    the keyboard column-reorder affordance.
                                    Dropping it (the other half of #415) would
                                    delete a working feature, so only the naming
                                    half is done here.

                                    WHY AN EMPTY role="img" AND NOT sr-only
                                    TEXT: this table is shared by ~122 files.
                                    Plain hidden text lands in `th.textContent`,
                                    and the repo asserts header identity through
                                    exactly that — `th.textContent.trim() ===
                                    'Deleted At'` in the recycle-bin spec, and
                                    anchored `hasText: /^name$/i` header filters
                                    across the recipients / groups / LMS /
                                    phishing E2E suites. All of them would break
                                    on appended text. An EMPTY element named by
                                    aria-label contributes to the accessible
                                    name computation of its ancestor while
                                    leaving textContent byte-identical. */}
                                <span
                                  role="img"
                                  aria-label={t("shell.table.dragToReorder")}
                                  className="sr-only"
                                />
                              </>
                            )}
                          </SortableTableHead>
                        );
                      })}
                      {/* Trailing filler header cell — absorbs leftover width
                          when the columns are narrower than the container so the
                          real columns keep their exact widths (no shrink/snap
                          between populated and empty states). width:auto with
                          no min lets table-fixed give it all the slack. */}
                    </TableRow>
                  </SortableContext>
                );
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const rowClassName =
                    typeof rowBehavior?.className === "function"
                      ? rowBehavior.className(row.original)
                      : (rowBehavior?.className ?? "");

                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      // `group/row` lets cell renderers reveal action icons on
                      // row hover via `group-hover/row:opacity-100`. The
                      // densityClass above enforces row height so the row
                      // doesn't grow when content wraps.
                      className={cn(
                        "group/row",
                        rowBehavior?.onClick && "cursor-pointer",
                        rowClassName,
                      )}
                      // #170: a clickable row must be keyboard-focusable, both so
                      // it can be activated without a mouse AND so a drawer/dialog
                      // opened from it returns focus HERE on close instead of
                      // dumping it on <body> (Radix restores focus to whatever was
                      // activeElement at open time). Scoped to rows that actually
                      // have an onClick so non-interactive tables are unaffected.
                      tabIndex={rowBehavior?.onClick ? 0 : undefined}
                      onKeyDown={
                        rowBehavior?.onClick
                          ? (e) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              // A key pressed in a portal (a row menu, a dialog
                              // opened from the row) bubbles here through React.
                              if (isFromOutsideRow(e)) return;
                              const target = e.target as HTMLElement;
                              const selector =
                                rowBehavior.interactiveSelector ??
                                DEFAULT_INTERACTIVE_SELECTOR;
                              // Let the key reach a real control inside the row.
                              if (target.closest(selector)) return;
                              e.preventDefault();
                              rowBehavior.onClick!(row.original);
                            }
                          : undefined
                      }
                      onClick={
                        rowBehavior?.onClick
                          ? (e) => {
                              // A click in a portal bubbles here through React —
                              // e.g. on a disabled row-menu item, which takes no
                              // pointer events, so the click lands on the menu
                              // content and used to open the record behind it.
                              if (isFromOutsideRow(e)) return;
                              const target = e.target as HTMLElement;
                              const selector =
                                rowBehavior.interactiveSelector ??
                                DEFAULT_INTERACTIVE_SELECTOR;
                              const interactive = target.closest(selector);
                              if (interactive) return;
                              rowBehavior.onClick!(row.original);
                            }
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          // `align-middle` keeps the checkbox + status icons
                          // baseline-aligned with text in sibling cells, even
                          // when one cell renders a taller spinner glyph (B4).
                          // `overflow-hidden` clips wrapping content so row
                          // height stays uniform.
                          className="first:ps-4 last:pe-4 align-middle overflow-hidden"
                          style={colWidthMeta.styleFor(cell.column.id)}
                        >
                          {/* Plain-text cells render as <UserText> (RTL-04). */}
                          {renderBodyCell(cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={columns.length}
                    className="h-[200px] p-0 border-0"
                  >
                    {/* Spacer cell only — the actual empty/no-results content is
                        rendered as an absolute overlay over the scroll body
                        (below), so it always centers in the VISIBLE viewport
                        regardless of how wide the table-fixed columns make the
                        row.
                        
                        The height is load-bearing, not decoration. An absolute
                        overlay cannot give its container height, so the ONLY
                        thing holding the scroll body open when the table is
                        empty is this row — and the density class applies to
                        every tbody row, this one included (compact h-8 = 32px,
                        and compact is the default). At `h-px` the ~150px
                        message was centred inside 32px and spilled over the
                        header and the pagination bar; reported on
                        /lms/reports/v2/departments as the empty state
                        "overlapping in the small row variant".
                        
                        Height on a table cell behaves as a minimum, so this
                        wins over the density utility without !important. It
                        only ever renders when there are no rows, so nothing
                        changes for a populated table. */}
                    &nbsp;
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>

          {/* Empty / no-results overlay — absolutely centered over the scroll
              body (which is `relative` + sized) so the content sits in the
              middle of the VISIBLE area below the frozen header, never pushed
              off-screen by a wide table-fixed row. Pinned under the sticky
              header via top-11 (header is h-11). */}
          {table.getRowModel().rows?.length === 0 && (
            <div className="pointer-events-none absolute inset-0 top-11 flex items-center justify-center px-4">
              <div className="pointer-events-auto flex flex-col items-center justify-center text-center">
                {/* Error state (#212) takes priority over every empty/no-results
                    body: when the fetch failed, the toolbar stays mounted above
                    (so the operator can change the query) and the BODY shows an
                    honest error + Retry — never a "no data" / "create your
                    first X" claim, which would misread a failure as an empty
                    workspace. */}
                {errorState ? (
                  <>
                    <AlertCircle className="h-10 w-10 text-destructive" />
                    <p className="text-sm font-medium mt-3">
                      {errorState.title ?? t("shell.table.errorTitle")}
                    </p>
                    {errorState.description && (
                      <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                        {errorState.description}
                      </p>
                    )}
                    {errorState.onRetry && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={errorState.onRetry}
                      >
                        <RotateCcw className="h-3.5 w-3.5 me-1.5" />
                        {t("common.retry")}
                      </Button>
                    )}
                  </>
                ) : /* Twin of the replace-mode guard above: only show the
                    first-time "create your first X" inline empty state when the
                    list is GENUINELY empty (no active search/filter). When a
                    search/filter emptied it, fall through to the
                    `hasActiveTableFilters` branch below so the user sees "No
                    results match your filters — Clear filters" with the search
                    box still visible, instead of a false "no data" claim. */
                emptyState?.mode === "inline" &&
                data.length === 0 &&
                !hasActiveTableFilters &&
                !isFetching ? (
                  <>
                    {emptyState.icon}
                    <p className="text-sm font-medium mt-2">{emptyState.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {emptyState.description}
                    </p>
                    {emptyState.actions && (
                      <div className="mt-4">{emptyState.actions}</div>
                    )}
                  </>
                ) : hasActiveTableFilters ? (
                  <>
                    <SearchX className="h-10 w-10 text-muted-foreground/60" />
                    <p className="text-sm font-medium mt-3">
                      {t("shell.table.noMatchesTitle")}
                    </p>
                    {/* "Try adjusting or clearing your search and filters" is
                        true of every table and therefore tells this operator
                        nothing. A page that knows WHICH filter emptied it —
                        the Feedback desk knows it is showing "tickets resolved
                        yesterday, for Acme" — says so instead. */}
                    <p className="text-xs text-muted-foreground mt-1 max-w-md text-balance">
                      {filteredEmptyHint ?? t("shell.table.noMatchesHint")}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={clearAllTableFilters}
                    >
                      <X className="h-3.5 w-3.5 me-1.5" />
                      {t("shell.table.clearFilters")}
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {noResultsText ?? t("shell.table.noResults")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeHeaderId ? (
            <div className="bg-background border rounded-md shadow-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap opacity-90">
              {getDisplayName(activeHeaderId)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Client-side pagination footer — driven by TanStack table state.
          Rendered when `clientPagination` is set AND server-side `pagination`
          is NOT (server wins). Reads filtered+sorted row count so the
          range matches what's actually displayed. */}
      {!pagination && clientPagination && (() => {
        const tablePageSize = table.getState().pagination.pageSize;
        const pageIndex = table.getState().pagination.pageIndex;
        const filteredCount = table.getFilteredRowModel().rows.length;
        const totalPages = Math.max(
          1,
          Math.ceil(filteredCount / Math.max(1, tablePageSize)),
        );
        const currentPage = pageIndex + 1;
        const start = filteredCount === 0 ? 0 : pageIndex * tablePageSize + 1;
        const end = Math.min((pageIndex + 1) * tablePageSize, filteredCount);
        const pageSizeOptions =
          clientPagination.pageSizeOptions ?? [10, 25, 50, 100];
        return (
          <div
            // #415 (b) — landing place for the skip link. Only ONE footer may
            // own the id: this client footer and the server/cursor footer below
            // can both render when a caller passes `clientPagination` AND
            // `renderFooterStatus`, and a duplicate id would make
            // getElementById pick whichever came first.
            id={renderFooterStatus ? undefined : paginationRegionId}
            tabIndex={renderFooterStatus ? undefined : -1}
            // Same FAB clearance as the server footer (#204/#230). Now that the
            // body grows, this pager sits at the card's bottom edge — where the
            // resting Feedback FAB is — instead of right under the rows.
            data-fab-footer=""
            className="flex items-center justify-between p-4 border-t text-sm"
          >
            <div className="text-muted-foreground text-xs">
              {totalPages <= 1 ? (
                <span>{countLabel(filteredCount)}</span>
              ) : (
                <span>{t("shell.table.range", { start, end, total: filteredCount })}</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t("common.rows")}</span>
                <Select
                  value={`${tablePageSize}`}
                  onValueChange={(value) => table.setPageSize(Number(value))}
                >
                  <SelectTrigger
                    className="h-7 w-20 text-xs"
                    aria-label={t("shell.table.rowsPerPage")}
                  >
                    <SelectValue>{tablePageSize}</SelectValue>
                  </SelectTrigger>
                  <SelectContent side="top">
                    {pageSizeOptions.map((size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">
                {t("shell.table.page", { page: currentPage, pages: totalPages })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  aria-label={t("shell.table.firstPage")}
                  title={t("shell.table.firstPage")}
                >
                  <ChevronsLeft className="h-3.5 w-3.5 rtl-flip" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label={t("shell.table.previousPage")}
                  title={t("shell.table.previousPage")}
                >
                  <ChevronLeft className="h-3.5 w-3.5 rtl-flip" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label={t("shell.table.nextPage")}
                  title={t("shell.table.nextPage")}
                >
                  <ChevronRight className="h-3.5 w-3.5 rtl-flip" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-xs"
                  onClick={() => table.setPageIndex(totalPages - 1)}
                  disabled={!table.getCanNextPage()}
                  aria-label={t("shell.table.lastPage")}
                  title={t("shell.table.lastPage")}
                >
                  <ChevronsRight className="h-3.5 w-3.5 rtl-flip" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer — shown when there's page-based pagination OR a custom
          renderFooterStatus (cursor-based tables, e.g. the phishing Activity
          report, render their "Load more" here and pass no `pagination` prop;
          gating the whole footer on `pagination` silently dropped it). The
          page-nav controls below still require `pagination` and are guarded. */}
      {(pagination || renderFooterStatus) && (
        <div
          // #415 (b) — landing place for the skip link. tabIndex={-1} makes it
          // a valid fragment target: without it an in-page anchor moves the
          // scroll position but leaves focus stranded back up in the table.
          id={paginationRegionId}
          tabIndex={-1}
          // #204/#230 — the FAB's corner band overlaps one end of this bar;
          // globals.css pads the end body[data-fab-reserve] names ("right" |
          // "left", or the default corner's while it is empty). Tagged
          // here rather than sized here so the clearance disappears with the
          // FAB, and so nothing about this element's HEIGHT ever changes.
          data-fab-footer=""
          // mt-auto pins this bar to the CARD's bottom edge. The card is a flex
          // column with a height in both modes (`flex-1 min-h-0` when filling the
          // viewport, `min-h-[600px]` otherwise), so a short table used to leave
          // the slack AFTER this bar: measured on /announcements/campaigns,
          // 2 rows in a 600px card put 283px of empty card BELOW the pagination,
          // which reads as the pager floating mid-table.
          //
          // Done here rather than by growing the scroll body with `flex-1
          // min-h-0`, which is the obvious fix and the wrong one: #587 (dac94dc6)
          // reverted exactly that because Courses and Enrollments collapsed to
          // 0px. (The body now grows with `grow` instead — basis auto,
          // positive space only — which cannot collapse it; see the scroll body.
          // This mt-auto therefore rarely has slack left; it stays as a guard.) `min-height: auto`
          // does not save a body that has `overflow: auto` — its automatic
          // minimum is 0 — so growing the body reopens that hole. Pushing the
          // footer touches nothing about how the body sizes: measured, the
          // squeezed-card case is identical with and without this.
          className="mt-auto flex items-center justify-between p-4 border-t text-sm"
        >
          <div className="text-muted-foreground text-xs">
            {(() => {
              // Bug #53 — when the "select all across pages" synthetic flag
              // is set, the rowSelection map only reflects the current page
              // (~25 rows), so using selectedRowCount would undercount in the
              // footer and contradict the banner (which correctly shows the
              // all-filtered count). Prefer filteredRowCount in that case.
              const effectiveSelectedCount = allFilteredRowsSelected
                ? filteredRowCount
                : selectedRowCount;
              if (renderFooterStatus) {
                return renderFooterStatus({
                  table,
                  selectedCount: effectiveSelectedCount,
                  // #256 — the selection intersected with the CURRENT
                  // filter/page. In all-across-pages mode every row on this
                  // page is part of the selection, so the synthetic total is
                  // the honest number and the caller should use that instead.
                  inScopeSelectedCount: allFilteredRowsSelected
                    ? effectiveSelectedCount
                    : selectedItems.length,
                  filteredCount: pagination?.total ?? data.length,
                  displayedCount: data.length,
                  totalCount: pagination?.total ?? data.length,
                });
              }
              // No server pagination (footer still renders for custom filters /
              // row selection). There are no pages to range over, so show a
              // simple selected-or-total count and bail before the pagination.*
              // dereferences below — which would otherwise NPE at runtime and
              // fail tsc ('pagination' is possibly 'undefined').
              if (!pagination) {
                return effectiveSelectedCount > 0 ? (
                  <span>{t("shell.table.selectedCount", { count: effectiveSelectedCount })}</span>
                ) : (
                  <span>{countLabel(data.length)}</span>
                );
              }
              const start = (pagination.page - 1) * pagination.pageSize + 1;
              const end = Math.min(
                start + data.length - 1,
                pagination.total,
              );
              if (effectiveSelectedCount > 0) {
                return (
                  <span>
                    {t("shell.table.selectedOfTotal", {
                      selected: effectiveSelectedCount,
                      total: pagination.total,
                    })}
                  </span>
                );
              }
              // STG-595 — an uncontrolled advanced search filters THIS page in
              // the browser; the server's range ("1-25 of 55") describes the
              // unfiltered page and contradicted "No results match". Say how
              // many rows of this page match instead. Same condition as
              // crossPageSelectUnsafe: a controlled search is the server's own
              // filter, so its range stands.
              if (crossPageSelectUnsafe) {
                return (
                  <span>
                    {t("shell.table.pageMatches", {
                      count: filteredData.length,
                      pageSize: data.length,
                    })}
                  </span>
                );
              }
              // FIX R1-m5 — hide range+rows-per-page counter when single page
              if (pagination.totalPages <= 1) {
                return <span>{countLabel(pagination.total)}</span>;
              }
              return (
                <span>{t("shell.table.range", { start, end, total: pagination.total })}</span>
              );
            })()}
          </div>
          {pagination && (
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t("common.rows")}</span>
              <Select
                value={`${pagination.pageSize}`}
                onValueChange={(value) =>
                  pagination.onPageSizeChange(Number(value))
                }
              >
                <SelectTrigger
                  className="h-7 w-20 text-xs"
                  aria-label={t("shell.table.rowsPerPage")}
                >
                  <SelectValue>{pagination.pageSize}</SelectValue>
                </SelectTrigger>
                <SelectContent side="top">
                  {(pagination.pageSizeOptions ?? [10, 25, 50, 100]).map(
                    (size) => (
                      <SelectItem key={size} value={`${size}`}>
                        {size}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground tabular-nums">
              {t("shell.table.page", {
                page: pagination.page,
                pages: Math.max(pagination.totalPages, 1),
              })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(1)}
                disabled={pagination.page <= 1}
                aria-label={t("shell.table.firstPage")}
                title={t("shell.table.firstPage")}
              >
                <ChevronsLeft className="h-3.5 w-3.5 rtl-flip" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                aria-label={t("shell.table.previousPage")}
                title={t("shell.table.previousPage")}
              >
                <ChevronLeft className="h-3.5 w-3.5 rtl-flip" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label={t("shell.table.nextPage")}
                title={t("shell.table.nextPage")}
              >
                <ChevronRight className="h-3.5 w-3.5 rtl-flip" />
              </Button>
              <Button
                variant="outline"
                size="icon-xs"
                onClick={() => pagination.onPageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label={t("shell.table.lastPage")}
                title={t("shell.table.lastPage")}
              >
                <ChevronsRight className="h-3.5 w-3.5 rtl-flip" />
              </Button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Advanced Search Dialog */}
      {advancedSearch &&
        advancedSearchState &&
        advancedSearch.renderDialog({
          open: advancedSearchOpen,
          onOpenChange: setAdvancedSearchOpen,
          searchState: advancedSearchState,
          onApply: setAdvancedSearchState,
          onClear: () => setAdvancedSearchState(advancedSearch.emptyState),
        })}

      {/* Bulk Action Confirmation Dialog */}
      {confirmAction?.confirm && (
        <AlertDialog
          open={!!confirmAction}
          onOpenChange={(open) => !open && setConfirmAction(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmAction.confirm.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction.confirm.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                className={
                  confirmAction.confirm.destructive
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : ""
                }
                onClick={() => {
                  confirmAction.onExecute(selectedItems, clearSelection, {
                    allFilteredSelected: allFilteredRowsSelected,
                    filteredTotal: filteredRowCount,
                  });
                  setConfirmAction(null);
                }}
              >
                {confirmAction.confirm.confirmLabel || t("common.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

